import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { api, WorkspacesAPITagType } from '@/api/api'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import defaultStringifyRequest from '@/constants/default-stringify-request'
import {
  demoComments,
  demoCrawlerStatus,
  demoFetchCommentBoardResponse,
  demoFetchCommentChartAnalysisResponse,
  demoReport,
  demoStatistics,
  demoTerms,
  demoTermsFrequency,
} from '@/constants/demo-report'
import { reduxAPI } from '@/store/redux-api'
import {
  AddReportKolHandfilledRequest,
  AddReportKolsRequest,
  AddTermsRequest,
  CommentBoardResponse,
  CommentChartAnalysisResponse,
  CommentRequest,
  CreateReportRequest,
  CreateReportResponse,
  DelereReportRequest,
  DeleteReportKolContentRequest,
  DeleteReportKolRequest,
  DeleteTermRequest,
  FetchCommentsRequest,
  FetchCommentsResponse,
  FetchReportCrawlerStatusRequest,
  FetchReportCrawlerStatusResponse,
  FetchReportOwnerListResponse,
  FetchReportRequest,
  FetchReportsRequest,
  FetchReportsResponse,
  FetchStatisticsRequest,
  FetchStatisticsResponse,
  FetchTermsFrequencyRequest,
  FetchTermsFrequencyResponse,
  FetchTermsRequest,
  FetchTermsResponse,
  FetReportPermissionRequest,
  FetReportPermissionResponse,
  PinCommentRequest,
  ReadCrawlerStatusRequest,
  TriggerRefreshReportRequest,
  UpdateReportIndustryRequest,
  UpdateReportKolHandfilledRequest,
  UpdateReportRequest,
  UpdateReportValuationRequest,
} from '@/types/api/report'
import {
  createReportResponseSchema,
  fetchCommentsResponseSchema,
  fetchReportCrawlerStatusResponseSchema,
  fetchStatisticsSchema,
  fetchTermsFrequencyResponseSchema,
  fetchTermsResponseSchema,
  fetReportPermissionResponseSchema,
} from '@/types/schema/api/report'
import { filterResourceType } from '@/types/schema/directory/kol-management'
import { Report } from '@/types/schema/report-schema'
import { camelizeKeys, decamelizeKeys } from '@/utils/humps'
import removeNil from '@/utils/remove-nil'

export const reportApi = api.injectEndpoints({
  endpoints: (build) => ({
    fetchCommentChartAnalysis: build.query<
      CommentChartAnalysisResponse,
      CommentRequest
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        if (arg.reportID === 'demo') {
          return {
            data: demoFetchCommentChartAnalysisResponse as CommentChartAnalysisResponse,
          }
        }

        const result = await baseQuery({
          url: getWorkspaceApiUrl(
            `/reports/${arg.reportID}/comment-sentiment-chart`,
          ),
          params: decamelizeKeys(arg),
        })

        return result.data
          ? {
              data: camelizeKeys(
                result.data as object,
              ) as unknown as CommentChartAnalysisResponse,
            }
          : { error: result.error as FetchBaseQueryError }
      },
    }),
    fetchCommentBoard: build.query<CommentBoardResponse, CommentRequest>({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        if (arg.reportID === 'demo') {
          return {
            data: demoFetchCommentBoardResponse as CommentBoardResponse,
          }
        }

        const result = await baseQuery({
          url: getWorkspaceApiUrl(
            `/reports/${arg.reportID}/comment-sentiment-board`,
          ),
          params: removeNil(decamelizeKeys(arg)),
        })

        return result.data
          ? {
              data: camelizeKeys(
                result.data as object,
              ) as unknown as CommentBoardResponse,
            }
          : { error: result.error as FetchBaseQueryError }
      },
      providesTags: [WorkspacesAPITagType.CommentBoard],
    }),
    pinComment: build.mutation<void, PinCommentRequest>({
      query: ({ reportID, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/reports/${reportID}/pinned-comments`),
          method: 'POST',
          body: decamelizeKeys(body),
        }
      },
      invalidatesTags: [WorkspacesAPITagType.CommentBoard],
    }),
    unpinComment: build.mutation<void, PinCommentRequest>({
      query: ({ reportID, ...body }) => {
        return {
          url: getWorkspaceApiUrl(`/reports/${reportID}/pinned-comments`),
          method: 'DELETE',
          body: decamelizeKeys(body),
        }
      },
      invalidatesTags: [WorkspacesAPITagType.CommentBoard],
    }),
    addReportKolHandfilled: build.mutation<void, AddReportKolHandfilledRequest>(
      {
        query: ({ reportId, reportKolId, platformType, ...body }) => {
          return {
            url: getWorkspaceApiUrl(
              `/reports/${reportId}/reportKOLs/${reportKolId}/handfilled`,
            ),
            method: 'POST',
            body: {
              ...body,
              platform_type: platformType,
            },
          }
        },
        invalidatesTags: [
          WorkspacesAPITagType.Report,
          WorkspacesAPITagType.ReportCrawlerStatus,
        ],
      },
    ),
    updateReportKolHandfilled: build.mutation<
      void,
      UpdateReportKolHandfilledRequest
    >({
      query: ({ reportId, reportKolId, ...payload }) => {
        return {
          url: getWorkspaceApiUrl(
            `/reports/${reportId}/reportKOLs/${reportKolId}/handfilled`,
          ),
          method: 'PATCH',
          body: payload,
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.Report,
        WorkspacesAPITagType.ReportCrawlerStatus,
      ],
    }),
    fetchReport: build.query<Report, FetchReportRequest>({
      async queryFn({ reportId }, { dispatch }, _extraOptions, baseQuery) {
        if (reportId === 'demo') {
          dispatch(
            reduxAPI.state.report.actions.setReport(demoReport as Report),
          )
          dispatch(
            reduxAPI.state.report.actions.setAllChartData({
              data: (demoReport as Report).reportStatistics ?? [],
            }),
          )

          return { data: demoReport as Report }
        }

        const result = await baseQuery(
          getWorkspaceApiUrl(`/reports/${reportId}`),
        )

        if (result.data) {
          const report = camelizeKeys(result.data) as unknown as Report

          dispatch(reduxAPI.state.report.actions.setReport(report))
          dispatch(
            reduxAPI.state.report.actions.setAllChartData({
              data: report.reportStatistics ?? [],
            }),
          )

          return {
            data: report,
          }
        }

        return { error: result.error as FetchBaseQueryError }
      },
      providesTags: [WorkspacesAPITagType.Report],
    }),
    fetchReports: build.query<FetchReportsResponse, FetchReportsRequest>({
      query: ({ ...payload }) => {
        const params = removeNil({
          ...payload,
          per_page: 10,
          stringify: true,
          array_format: 'bracket',
        })
        return {
          url: getWorkspaceApiUrl('/reports'),
          params,
        }
      },
      transformResponse: (response: object) => {
        return camelizeKeys(response) as unknown as FetchReportsResponse
      },
      providesTags: [WorkspacesAPITagType.ReportList],
    }),
    fetchReportOwnerList: build.query<FetchReportOwnerListResponse, void>({
      query: () => {
        return {
          url: getWorkspaceApiUrl('/report-owners'),
        }
      },
    }),
    fetchReportCrawlerStatus: build.query<
      FetchReportCrawlerStatusResponse,
      FetchReportCrawlerStatusRequest
    >({
      async queryFn(
        { reportId, ...payload },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) {
        if (reportId === 'demo') {
          return { data: demoCrawlerStatus }
        }

        const params = {
          ...payload,
          ...defaultStringifyRequest,
          unread: false,
          statuses: ['running', 'failed', 'succeeded'],
        }
        const result = await baseQuery({
          url: getWorkspaceApiUrl(`/reports/${reportId}/crawler-status`),
          params,
        })
        if (result.data) {
          return {
            data: result.data as unknown as FetchReportCrawlerStatusResponse,
          }
        }

        return { error: result.error as FetchBaseQueryError }
      },
      extraOptions: {
        dataSchema: fetchReportCrawlerStatusResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.ReportCrawlerStatus],
    }),
    triggerRefreshReport: build.mutation<void, TriggerRefreshReportRequest>({
      query: ({ reportId }) => ({
        url: getWorkspaceApiUrl(`/reports/${reportId}/trigger-crawler`),
        method: 'POST',
      }),
      invalidatesTags: [WorkspacesAPITagType.ReportCrawlerStatus],
    }),
    fetchReportPermission: build.query<
      FetReportPermissionResponse,
      FetReportPermissionRequest
    >({
      query: ({ reportId }) => {
        return {
          url: getWorkspaceApiUrl(`/reports/${reportId}/permission`),
        }
      },
      extraOptions: {
        dataSchema: fetReportPermissionResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.ReportPermission],
    }),
    createReport: build.mutation<CreateReportResponse, CreateReportRequest>({
      query: ({ ...payload }) => {
        return {
          url: getWorkspaceApiUrl('/reports'),
          method: 'POST',
          body: payload,
        }
      },
      extraOptions: {
        dataSchema: createReportResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.ReportList,
        {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.deep_report,
        },
      ],
    }),
    deleteReport: build.mutation<void, DelereReportRequest>({
      query: ({ reportId }) => ({
        url: getWorkspaceApiUrl(`/reports/${reportId}`),
        method: 'DELETE',
      }),
      invalidatesTags: [
        WorkspacesAPITagType.ReportList,
        {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.deep_report,
        },
      ],
    }),
    updateReport: build.mutation<void, UpdateReportRequest>({
      query: ({ reportId, ...payload }) => ({
        url: getWorkspaceApiUrl(`/reports/${reportId}`),
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [WorkspacesAPITagType.Report],
    }),
    updateReportIndustry: build.mutation<void, UpdateReportIndustryRequest>({
      query: (payload) => ({
        url: getWorkspaceApiUrl('/industryData'),
        method: 'PUT',
        params: { reportID: payload.reportId },
        body: payload,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.Report,
        WorkspacesAPITagType.ReportStatistic,
      ],
    }),
    deleteReportKol: build.mutation<void, DeleteReportKolRequest>({
      query: ({ reportKolId, reportId }) => ({
        url: getWorkspaceApiUrl(
          `/reports/${reportId}/reportKOLs/${reportKolId}`,
        ),
        method: 'DELETE',
      }),
      invalidatesTags: [WorkspacesAPITagType.Report],
    }),
    deleteReportKolContent: build.mutation<void, DeleteReportKolContentRequest>(
      {
        query: ({ reportKolId, reportId, handfilledId }) => ({
          url: getWorkspaceApiUrl(
            `/reports/${reportId}/reportKOLs/${reportKolId}/handfilled/${handfilledId}`,
          ),
          method: 'DELETE',
        }),
        invalidatesTags: [WorkspacesAPITagType.Report],
      },
    ),
    addReportKols: build.mutation<void, AddReportKolsRequest>({
      query: ({ reportId, uuids }) => {
        return {
          url: getWorkspaceApiUrl(`/reports/${reportId}/reportKOLs`),
          method: 'POST',
          body: { reportId, uuids },
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.Report,
        WorkspacesAPITagType.ReportCrawlerStatus,
        WorkspacesAPITagType.KolManagement,
        WorkspacesAPITagType.KolCollectionKols,
      ],
    }),
    updateReportValuation: build.mutation<void, UpdateReportValuationRequest>({
      query: ({ reportId, valuation }) => ({
        url: getWorkspaceApiUrl(`/reports/${reportId}/valueEstimationWeight`),
        method: 'PUT',
        body: valuation,
      }),
      invalidatesTags: [WorkspacesAPITagType.Report],
    }),
    fetchStatistics: build.query<
      FetchStatisticsResponse,
      FetchStatisticsRequest
    >({
      async queryFn({ reportId }, _queryApi, _extraOptions, baseQuery) {
        if (reportId === 'demo') {
          return { data: demoStatistics as FetchStatisticsResponse }
        }

        const result = await baseQuery({
          url: getWorkspaceApiUrl('/statistics'),
          params: { reportID: reportId },
        })

        if (result.data) {
          return {
            data: result.data as unknown as FetchStatisticsResponse,
          }
        }

        return { error: result.error as FetchBaseQueryError }
      },
      extraOptions: {
        dataSchema: fetchStatisticsSchema,
      },
      providesTags: [WorkspacesAPITagType.ReportStatistic],
    }),
    fetchComments: build.query<FetchCommentsResponse, FetchCommentsRequest>({
      async queryFn(
        { ReportID, ...payload },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) {
        if (ReportID === 'demo') {
          return {
            data: demoComments,
          }
        }

        const result = await baseQuery({
          url: getWorkspaceApiUrl('/comment'),
          params: { ReportID, ...payload },
        })

        if (result.data) {
          return {
            data: result.data as unknown as FetchCommentsResponse,
          }
        }

        return { error: result.error as FetchBaseQueryError }
      },
      extraOptions: {
        dataSchema: fetchCommentsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.CommentBoard],
    }),
    fetchTerms: build.query<FetchTermsResponse, FetchTermsRequest>({
      async queryFn(
        { reportID, ...payload },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) {
        if (reportID === 'demo') {
          return {
            data: demoTerms,
          }
        }

        const result = await baseQuery({
          url: getWorkspaceApiUrl('/terms'),
          params: { reportID, ...payload },
        })

        if (result.data) {
          return {
            data: result.data as unknown as FetchTermsResponse,
          }
        }

        return { error: result.error as FetchBaseQueryError }
      },
      extraOptions: {
        dataSchema: fetchTermsResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.ReportTerms],
    }),
    fetchTermsFrequency: build.query<
      FetchTermsFrequencyResponse,
      FetchTermsFrequencyRequest
    >({
      async queryFn(
        { ReportID, ...payload },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) {
        if (ReportID === 'demo') {
          return {
            data: demoTermsFrequency,
          }
        }

        const result = await baseQuery({
          url: getWorkspaceApiUrl('/terms/ds/frequency'),
          params: { ReportID, ...payload },
        })

        if (result.data) {
          return {
            data: result.data as unknown as FetchTermsFrequencyResponse,
          }
        }

        return { error: result.error as FetchBaseQueryError }
      },
      extraOptions: {
        dataSchema: fetchTermsFrequencyResponseSchema,
      },
    }),
    readCrawlerStatus: build.mutation<void, ReadCrawlerStatusRequest>({
      query: (payload) => ({
        url: getWorkspaceApiUrl('/read-crawler-status'),
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [
        WorkspacesAPITagType.ReportCrawlerStatus,
        WorkspacesAPITagType.ReportList,
      ],
    }),
    addTerms: build.mutation<void, AddTermsRequest>({
      query: ({ reportID, term, isInclude }) => ({
        url: getWorkspaceApiUrl('/terms'),
        method: 'POST',
        body: { term, isInclude },
        params: { reportID },
      }),
      invalidatesTags: [WorkspacesAPITagType.ReportTerms],
    }),
    deleteTerms: build.mutation<void, DeleteTermRequest>({
      query: ({ termID }) => ({
        url: getWorkspaceApiUrl('/terms'),
        method: 'DELETE',
        params: { termID },
      }),
      invalidatesTags: [WorkspacesAPITagType.ReportTerms],
    }),
  }),
  overrideExisting: false,
})

export const {
  useFetchCommentChartAnalysisQuery,
  useFetchCommentBoardQuery,
  usePinCommentMutation,
  useUnpinCommentMutation,
  useAddReportKolHandfilledMutation,
  useUpdateReportKolHandfilledMutation,
  useFetchReportQuery,
  useFetchReportsQuery,
  useFetchReportOwnerListQuery,
  useFetchReportCrawlerStatusQuery,
  useTriggerRefreshReportMutation,
  useFetchReportPermissionQuery,
  useCreateReportMutation,
  useDeleteReportMutation,
  useUpdateReportMutation,
  useUpdateReportIndustryMutation,
  useDeleteReportKolMutation,
  useDeleteReportKolContentMutation,
  useAddReportKolsMutation,
  useUpdateReportValuationMutation,
  useFetchStatisticsQuery,
  useFetchCommentsQuery,
  useFetchTermsQuery,
  useFetchTermsFrequencyQuery,
  useReadCrawlerStatusMutation,
  useAddTermsMutation,
  useDeleteTermsMutation,
} = reportApi
